import { SymphonyRealmEnum } from '@amzn/journeys-common';
import type { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { useEffect, useMemo } from 'react';
import { useRealm } from '../../../../../useRealm';

/**
 * Mapping of Symphony realms to their corresponding service endpoints.
 */
const REALM_TO_SERVICE_URL = {
  [SymphonyRealmEnum.NA]: 'https://senna.us-east-1.prod.p13n.deals.amazon.dev/',
  [SymphonyRealmEnum.FE]: 'https://senna.us-west-1.prod.p13n.deals.amazon.dev/',
  [SymphonyRealmEnum.EU]: 'https://senna.eu-west-1.prod.p13n.deals.amazon.dev/',
  [SymphonyRealmEnum.TEST]: 'https://senna.us-east-1.prod.p13n.deals.amazon.dev/',
} as const;

/**
 * Custom widget for automatically populating the service URL based on campaign realm.
 *
 * **This is a Deals Campaign-Specific Widget**
 *
 * This widget is a bespoke solution for Deals campaigns that automatically derives the correct service URL
 * based on the campaign's realm. By using a hidden custom widget, we avoid adding a redundant realm field
 * to the JSON schema that would later need to be removed when persisting the campaign data, and the need for complex data
 * transformation logic.
 *
 * A tech debt ticket exists here for establishing a reusable pattern for doing this, instead of relying on bespoke widgets:
 * https://taskei.amazon.dev/tasks/P328799014
 */
export const HiddenServiceUrlWidget = <T = never, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = never>(
  props: WidgetProps<T, S, F>,
) => {
  const { onChange, value } = props;
  const { realm } = useRealm();

  const serviceUrl = useMemo(() => {
    if (!realm || realm === SymphonyRealmEnum.CN) {
      // Component should only render if a realm has been selected. CN is not currently an option in the UI.
      throw new Error(`No service URL set for realm: ${realm}`);
    }
    return REALM_TO_SERVICE_URL[realm];
  }, [
    realm,
  ]);

  useEffect(() => {
    if (serviceUrl && serviceUrl !== value) {
      onChange(serviceUrl);
    }
  }, [
    serviceUrl,
    value,
    onChange,
  ]);

  return <input type="hidden" value={serviceUrl || ''} />;
};
